
import { namespace, Getter } from "vuex-class";
import { Component, Prop, Watch, Emit, Mixins } from "vue-property-decorator";
import { OptionalColumn } from "@/types/tables/OptionalColumns";
import { Modules } from "@/store/ModuleTypes";
import OptionalColumnsMixin from '@/mixins/OptionalColumnsMixin';

const Devices = namespace(Modules.Devices);

@Component
export default class ChildDeviceTableColumnsModal extends Mixins(OptionalColumnsMixin) {
  @Prop({ default: true, type: Boolean }) public visible!: boolean;
  @Prop({ default: true, type: Boolean }) public showPlottedToggle: boolean;

  @Getter("getFeature") getFeature: (featureName: string[]) => boolean;
  @Devices.State("showPlottedLocation") public showPlottedLocation!: boolean;
  @Devices.State("showGroupSyncTitle") showGroupSyncTitle!: boolean;
  @Devices.Mutation("togglePlottedLocation") public togglePlottedLocation!: () => void;
  @Devices.Mutation("toggleGroupSyncTitle") toggleGroupSyncTitle!: () => void;

  public columnVisibilityState: Record<string, boolean> = {};
  private readonly columnKeys = {
    Plotted: "plotted",
    GroupSyncTitle: "groupSyncTitle",
  } as const;

  private isStateModified: boolean = false;

  private mounted(): void {
    if (this.visible) {
      this.onVisibleChanged(true);
    }
  }

  public get optionalColumns(): OptionalColumn[] {
    const columns: OptionalColumn[] = [];
    if (this.showPlottedToggle && this.isPlottedLocationFeatureEnabled) {
      columns.push({
        key: this.columnKeys.Plotted,
        label: "Plotted"
      });
    }

    if (this.isGroupSyncColumnEnabled) {
      columns.push({
        key: this.columnKeys.GroupSyncTitle,
        label: "Area Sync",
      });
    }
    
    return columns;
  }

  @Watch("visible")
  protected onVisibleChanged(val: boolean): void {
    if (val) {
      this.isStateModified = false;
      this.columnVisibilityState = {};

      this.optionalColumns.forEach(col => {
        switch (col.key) {
          case this.columnKeys.Plotted:
            if (this.showPlottedToggle) {
              this.$set(this.columnVisibilityState, col.key, this.showPlottedLocation);
            }
            break;
          case this.columnKeys.GroupSyncTitle:
            this.$set(this.columnVisibilityState, col.key, this.showGroupSyncTitle);
            break;
          default:
            this.$set(this.columnVisibilityState, col.key, false);
        }
      });
    }
  }

  @Emit("get-devices")
  public applyColumnChanges(): void {
    this.optionalColumns.forEach(col => {
      if (this.showPlottedToggle && col.key === this.columnKeys.Plotted && this.columnVisibilityState[col.key] !== this.showPlottedLocation) {
        this.togglePlottedLocation();
      }

      if (col.key === this.columnKeys.GroupSyncTitle && this.columnVisibilityState[col.key] !== this.showGroupSyncTitle) {
        this.toggleGroupSyncTitle();
      }
    });

    this.handleClose();
  }

  /**
  * @summary Checks if any checkbox state has changed compared to the Vuex state
  * and sets isStateModified to true if any change is detected, otherwise false.
  */
  public setStateModified(): void {
    var checkboxChanged = false;

    this.optionalColumns.forEach(col => {
      if (this.showPlottedToggle && col.key === this.columnKeys.Plotted && this.columnVisibilityState[col.key] !== this.showPlottedLocation) {
        checkboxChanged = true;
      }

      if (col.key === this.columnKeys.GroupSyncTitle && this.columnVisibilityState[col.key] !== this.showGroupSyncTitle) {
        checkboxChanged = true;
      }
    });

    this.isStateModified = checkboxChanged;
  }

  private handleClose(): void {
    this.$emit("close");
  }

  /**
	* @summary Returns true if all child column features are disabled,
	* which would cause the columns modal to appear empty.
	*/
  private get isModalEmpty(): boolean {
    const columnFeatures = [
      this.isGroupSyncColumnEnabled,
      this.showPlottedToggle && this.isPlottedLocationFeatureEnabled,
    ];

    return !columnFeatures.some(feature => feature);
  }
}
